import { Component, OnInit, EventEmitter, Input, Output, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';                  // 03/12/2021 RA
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { UserService } from '../../services/user.service';
import { PaymentService } from '../../services/payment.service';
import { ReferenceService } from '../../services/reference.service';

import { AutoPaymentActionType, AutoPaymentSelected } from '../payment-automatic-note-list/payment-automatic-note-list.component';
import { NoteAutoPayInformation, AutoPaymentDetail, PaymentType, BankAccountType, BraintreeCreditCardType } from '../../models/payment';
import { StatesListItem, CountryListItem, CardExpireMonthYearListItem, VerifyListItem} from '../../models/reference';  // 03/12/2021 RA
import { CompanyInformation } from '../../models/company';          // 10/11/2021
import { CompanyService } from '../../services/company.service';    // 10/11/2021

import * as braintree from 'braintree-web';
import { BraintreeService } from 'src/app/services/braintree.service';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-payment-automatic-form',
  templateUrl: './payment-automatic-form.component.html',
  styleUrls: ['./payment-automatic-form.component.scss']
})
export class PaymentAutomaticFormComponent implements OnInit {
  private _participantId: number = null;
  private companyCode: string = null;                               // 10/11/2021
  private _autoPaymentSelected: AutoPaymentSelected = {
    autoPaymentActionType: null,
    selectedId: null,
    selectedData: null
  };

  // Required to enable/disable 'Submit Payment' button (Mixture of Reactive controls and non-Reactive controls)
  @ViewChild('SubmitButton', { read: ElementRef, static: false }) SubmitButton: ElementRef<HTMLButtonElement>;

  @Input()
  set participantId(participantId: number) {
    this._participantId = participantId || null;
    this.getData();
  }
  get participantId(): number {
    return this._participantId;
  }

  @Input()
  set autoPaymentSelected(autoPaymentSelected: AutoPaymentSelected) {
    this._autoPaymentSelected = autoPaymentSelected || {
      autoPaymentActionType: null,
      selectedId: null,
      selectedData: null
    };
    this.getData();

    // When changing notes, let teardown Braintree's hosted fields allowing different program loans.
    // Also, if we don't tear down the fields, we will receive duplicate Iframe errors.
    if (this._autoPaymentSelected.selectedData != null ) {
      if (this._brainTreeUIRenderComplete) {
        this._brainTreeUIRenderComplete = false;
        this._braintreeService.tearDownBraintree(this._hostedFieldsInstance);
      }
    }
  }
  get autoPaymentSelected(): AutoPaymentSelected {
    return this._autoPaymentSelected;
  }

  @Output() dataComplete = new EventEmitter<any>();
  @Output() setupAutoPayment = new EventEmitter<AutoPaymentDetail>();
  @Output() cancelAutoPayment = new EventEmitter<AutoPaymentDetail>();

  private _handleError: HandleError;
  private _logging = environment.logging;

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  fieldValidations = environment.fieldValidations;

  showSpinner = false;
  allowPaymentInfoEdit = false;
  noteAutoPayInformationFound = false;
  noteAutoPayInformation: NoteAutoPayInformation = null;
  noteId: number = null;

  getNoteAutoPayInformationComplete = false;
  getCreditCardExpirationMonthListComplete = false;

  autoPaymentActionType = AutoPaymentActionType;
  paymentType = PaymentType;
  bankAccountType = BankAccountType;

  getStateListComplete = false;
  stateList: StatesListItem[] = [];
  showStateListSpinner = false;
  // 03/12/2021 RA
  getCountryListComplete = false;
  countryList: CountryListItem[] = [];
  showCountryListSpinner = false;
  // 04/08/2021 RA
  verifySt_CntyListComplete = false;
  verifyst_cntry: VerifyListItem = null;

  creditCardExpirationMonthList: CardExpireMonthYearListItem[] = [];
  creditCardExpMonthList: string[] = [];
  showCreditCardExpirationMonthListSpinner = false;

  paymentAutomaticForm: UntypedFormGroup;
  nameFormControl = new UntypedFormControl();
  addressLine1FormControl = new UntypedFormControl();
  addressLine2FormControl = new UntypedFormControl();
  cityFormControl = new UntypedFormControl();
  countryFormControl = new UntypedFormControl();             // 03/12/2021 RA
  stateFormControl = new UntypedFormControl();
  zipCodeFormControl = new UntypedFormControl();
  paymentTypeFormControl = new UntypedFormControl();
  bankRoutingNumberFormControl = new UntypedFormControl();
  bankAccountNumberFormControl = new UntypedFormControl();
  bankAccountNumberConfirmFormControl = new UntypedFormControl();
  bankAccountNumberEqualsFormControl = new UntypedFormControl();
  bankAccountTypeFormControl = new UntypedFormControl();
  companyInformation: CompanyInformation;            // 10/11/2021

  // Braintree
  private _clientToken: string;
  private _hostedFieldsInstance?: braintree.HostedFields;
  private _dataCollectorInstance?: braintree.DataCollector;
  private _brainTreeLenderProgramCode: string;
  private _brainTreeFieldFocus: string;
  private _brainTreeUIRenderComplete: boolean = false;
  private _brainTreeLastPaymentType: PaymentType = null;
  private _brainTreeFieldsValid: boolean = false;
  public showBraintreeSpinner: boolean = false;
  private _isSubmitted: boolean = false;

constructor(
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _paymentService: PaymentService,
    private _referenceService: ReferenceService,
    public media: MediaObserver,
    private _companyService: CompanyService,            // 10/11/2021
    private _braintreeService: BraintreeService,
    private cdr: ChangeDetectorRef,
   ) {
    this._handleError = _errorHandler.createHandleError('PaymentAutomaticFormComponent');
    const currentDate = new Date();
}

ngOnInit() {}

getData(): void {
  this.showSpinner = true;

  // 10/11/2021
  this.companyInformation = this._companyService.getNewCompanyInformation();
  this.companyCode =  this._companyService.getCompanyCode();

  this._companyService.companyInformation.subscribe(
    response => (this.companyInformation = response),
    error => this._handleError('companyInformation.subscribe', error)
  );

  this.getStateListComplete = false;
  this.getCountryListComplete = false;
  this.getNoteAutoPayInformationComplete = false;
  this.getCreditCardExpirationMonthListComplete = false;

  this.getStateList();
  this.getCountryList();                     // 03/12/2021 RA
  this.getNoteAutoPayInformation();          // 03/15/2021 RA
  this.getCreditCardExpirationMonthList();
}

getStateList(): void {
  this.showSpinner = true;
  this.getStateListComplete = false;
  this.stateList = [];

  this._referenceService.getStatesList().subscribe(
      resultArray => {
        this.stateList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.getStateList :: resultArray.length= ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentAutomaticFormComponent.getStateList :: resultArray = ' + JSON.stringify(resultArray));
        }
        this.getStateListComplete = true;
      },
      error => this._handleError('getStateList', error)
  );
}

// 03/16/2021 RA Get State list filtered on Country Code
getStateCountryList(country: string = null): void {
  this.getStateListComplete = false;
  this.stateList = [];

  this._referenceService.getStatesCountryList(country).subscribe(
      resultArray => {
        this.stateList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.getStateCountryList :: resultArray.length= ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentAutomaticFormComponent.getStateCountryList :: resultArray = ' + JSON.stringify(resultArray));
        }
        this.getStateListComplete = true;
      },
      error => this._handleError('getStateCountryList', error)
  );
}

// 03/12/2021 RA
getCountryList(): void {
  this.showSpinner = true;
  this.getCountryListComplete = false;
  this.countryList = [];

  this._referenceService.getCountryList().subscribe(
      resultArray => {
        this.countryList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.getCountryList :: resultArray.length= ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentAutomaticFormComponent.getCountryList :: resultArray = ' + JSON.stringify(resultArray));
        }
        this.getCountryListComplete = true;
      },
      error => this._handleError('getCountryList', error)
    );
}

// 04/08/2021 RA Verify Country Code for State provided
verifyState_Country(state: string = null, country: string = null): void {
    this.verifySt_CntyListComplete = false;

    this._referenceService.verifyStateCountry(state, country).subscribe(
      result => {
        this.verifySt_CntyListComplete = result !== null && result.countryCode != null;
        this.verifyst_cntry = result;
        // 04/12/2021 RA - To null out the state field if it's not valid
        if (this.verifySt_CntyListComplete === false) {
          this.paymentAutomaticForm.get('stateFormControl').setValue(null);
        } else {
          // 04/19/2021 RA - Add state to field so it will be populated if valid
          this.paymentAutomaticForm.get('stateFormControl').setValue(state);
        }
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.verifyState_Country :: resultArray.length= ' + result);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('PaymentAutomaticFormComponent.verifyState_Country :: resultArray = ' + JSON.stringify(result));
        }
      },
      error => this._handleError('verifyState_Country', error)
    );
}

getNoteAutoPayInformation(): void {
  this.showSpinner = true;
  this.getNoteAutoPayInformationComplete = false;
  if (
     this.participantId !== null &&
     this._autoPaymentSelected !== null &&
     this._autoPaymentSelected.selectedData !== undefined &&
     this._autoPaymentSelected.selectedData !== null
    ) {
       this.noteId = this._autoPaymentSelected.selectedData.noteId;
       this._paymentService.getNoteAutoPayInformation(this.participantId, this.noteId).subscribe(
       result => {
          this.noteAutoPayInformation = result;
          if (this._logging.serviceCallTraceLog) {
            console.log('PaymentAutomaticFormComponent.getNoteAutoPayInformation :: result.noteId= ' + result.noteId);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('PaymentAutomaticFormComponent.getNoteAutoPayInformation :: result = ' + JSON.stringify(result));
          }

          this.noteAutoPayInformationFound = true;
          this.getNoteAutoPayInformationComplete = true;
          this.createForm();
          this.getDataComplete();
        },
        error => this._handleError('getNoteAutoPayInformation', error)
      );
    } else {
      this.noteAutoPayInformationFound = false;
      this.noteAutoPayInformation = null;
      this.getDataComplete();
    }
}

getCreditCardExpirationMonthList(): void {
  this.showCreditCardExpirationMonthListSpinner = true;
  this.getCreditCardExpirationMonthListComplete = false;
  this.creditCardExpirationMonthList = [];

  this._referenceService.getCardExpireMonthList().subscribe(
  resultArray => {
      this.creditCardExpirationMonthList = resultArray;
      if (this._logging.serviceCallTraceLog) {
        console.log('PaymentAutomaticFormComponent.getCreditCardExpirationMonthList :: resultArray.length= ' + resultArray.length);
      }
      if (this._logging.serviceDataTraceLog) {
        console.log('PaymentAutomaticFormComponent.getCreditCardExpirationMonthList :: resultArray = ' + JSON.stringify(resultArray));
      }

      // Braintree requires an array of strings; months are prefixed with zero. Braintree are single digits
      resultArray.forEach(m => {
        this.creditCardExpMonthList.push(m.expireValue);
      });

      this.showCreditCardExpirationMonthListSpinner = false;
      this.getCreditCardExpirationMonthListComplete = true;
  },
    error => this._handleError('getCreditCardExpirationMonthList', error)
  );
}

getDataComplete(): void {
  this.showSpinner = false;

  if (
    this.getStateListComplete &&
    this.getCountryListComplete &&            // 03/12/2021 RA
    this.getNoteAutoPayInformationComplete  &&
    this.getCreditCardExpirationMonthListComplete
  ) {
     this.dataComplete.emit(true);
    }
}

createForm(): void {
    this.paymentAutomaticForm = this._formBuilder.group({});
    this.paymentAutomaticForm.addControl('nameFormControl', this.nameFormControl);
    this.paymentAutomaticForm.addControl('addressLine1FormControl', this.addressLine1FormControl);
    this.paymentAutomaticForm.addControl('addressLine2FormControl', this.addressLine2FormControl);
    this.paymentAutomaticForm.addControl('cityFormControl', this.cityFormControl);
    this.paymentAutomaticForm.addControl('stateFormControl', this.stateFormControl);
    this.paymentAutomaticForm.addControl('zipCodeFormControl', this.zipCodeFormControl);
    this.paymentAutomaticForm.addControl('countryFormControl', this.countryFormControl);          // 03/12/2021 RA
    this.paymentAutomaticForm.addControl('paymentTypeFormControl', this.paymentTypeFormControl);
    this.paymentAutomaticForm.addControl('bankRoutingNumberFormControl', this.bankRoutingNumberFormControl);
    this.paymentAutomaticForm.addControl('bankAccountNumberFormControl', this.bankAccountNumberFormControl);
    this.paymentAutomaticForm.addControl('bankAccountNumberConfirmFormControl', this.bankAccountNumberConfirmFormControl);
    this.paymentAutomaticForm.addControl('bankAccountNumberEqualsFormControl', this.bankAccountNumberEqualsFormControl);
    this.paymentAutomaticForm.addControl('bankAccountTypeFormControl', this.bankAccountTypeFormControl);

    this.paymentAutomaticForm.setValue({
      nameFormControl: this.noteAutoPayInformation.name,
      addressLine1FormControl: this.noteAutoPayInformation.addressLine1,
      addressLine2FormControl: this.noteAutoPayInformation.addressLine2,
      cityFormControl: this.noteAutoPayInformation.city,
      countryFormControl: this.noteAutoPayInformation.country,          // 03/12/2021 RA
      stateFormControl: this.noteAutoPayInformation.state,
      zipCodeFormControl: this.noteAutoPayInformation.zipCode,
      paymentTypeFormControl: this.noteAutoPayInformation.acceptCreditCardFlag
        ? PaymentType.CreditCard.valueOf()
        : PaymentType.ElectronicCheck.valueOf(),
      bankRoutingNumberFormControl: '',
      bankAccountNumberFormControl: '',
      bankAccountNumberConfirmFormControl: '',
      bankAccountNumberEqualsFormControl: true,
      bankAccountTypeFormControl: BankAccountType.Checking.valueOf()
    });

    // Create Braintree's HostedFields when accepting credit cards
    if (this.noteAutoPayInformation.acceptCreditCardFlag &&
        this.autoPaymentSelected.autoPaymentActionType === AutoPaymentActionType.Setup) {
        this.createBrainTreeHostedFields();
    }

    // 03/23/2021 RA - Reload State list to include filter by country.
    this.getStateCountryList(this.paymentAutomaticForm.get('countryFormControl').value);

    // 04/08/2021 RA - Verify the State/Country combination.
    this.verifyState_Country(this.paymentAutomaticForm.get('stateFormControl').value,
                             this.paymentAutomaticForm.get('countryFormControl').value);

    // 04/13/2021 RA - To verify if the State/Country combination is valid and if not then mark State as invalid
    if (this.verifySt_CntyListComplete === false) {
      this.paymentAutomaticForm.get('stateFormControl').markAsTouched();
    }

    if (this.autoPaymentSelected.autoPaymentActionType === AutoPaymentActionType.Setup) {
      this.paymentAutomaticForm.get('nameFormControl').setValidators([Validators.required]);
      this.paymentAutomaticForm.get('addressLine1FormControl').setValidators([Validators.required]);
      this.paymentAutomaticForm.get('cityFormControl').setValidators([Validators.required]);
      this.paymentAutomaticForm.get('stateFormControl').setValidators([Validators.required]);
      this.paymentAutomaticForm.get('countryFormControl').setValidators([Validators.required]);             // 04/08/2021 RA - Added

      // 03/12/2021 RA - Check the Zip Code validation - passing the country code
      this.setPaymentZipCodeValidation(this.paymentAutomaticForm.get('countryFormControl').value);

      // 04/08/2021 RA - Mark Country field as touch to trigger validation of the field to make sure it's valid.
      this.paymentAutomaticForm.get('countryFormControl').markAsTouched();


      // 03/23/2021 RA - Need to update the values after filling the drop down boxes and validate data
      this.paymentAutomaticForm.get('countryFormControl').updateValueAndValidity();
      this.paymentAutomaticForm.get('stateFormControl').updateValueAndValidity();
      this.paymentAutomaticForm.get('zipCodeFormControl').updateValueAndValidity();

      // TA - 06/06/2023 - Not Necessary
      //this.paymentTypeChanged(this.paymentTypeFormControl.value);

      // TA - 06/06/2023 - Getting called many times for same payment; Just get distinct value and process;
      //                   When switching payment types; We will need to render hosted fields again.
      this.paymentAutomaticForm.get('paymentTypeFormControl').valueChanges
                                .pipe(distinctUntilChanged())
                                .subscribe(val => {
                                    this.paymentTypeChanged(val);
                                    this._brainTreeLastPaymentType = val;
      });

      this.paymentAutomaticForm.get('bankAccountNumberFormControl').valueChanges.subscribe(val => {
        this.bankAccountNumberConfirmChanged(val);
      });

      this.paymentAutomaticForm.get('bankAccountNumberConfirmFormControl').valueChanges.subscribe(val => {
        this.bankAccountNumberConfirmChanged(val);
      });

      // 03/12/2021 This will add a value change listener to the country form control -
      //            the code inside will get called with the user changes value for country
      // 06/06/2023 - TA - Getting called many times causing call stack to exceed size. Process distinct values.
      this.paymentAutomaticForm.get('countryFormControl').valueChanges
                               .pipe(distinctUntilChanged())
                               .subscribe(val => {
                                  this.setPaymentZipCodeValidation(val);
                                  this.reset_state(val);
      });

      // 03/11/2021 This will add a value change listener to the zip code form control -
      //            the code inside will get called with the user changes value for zip code
      // 06/06/2023 - TA - Getting called many times causing call stack to exceed size. Process distinct values.
      this.paymentAutomaticForm.get('zipCodeFormControl').valueChanges
                               .pipe(distinctUntilChanged())
                               .subscribe(val => {
                                  this.setPaymentZipCodeValidation(this.paymentAutomaticForm.get('countryFormControl').value);
      });

      // 03/12/2021 RA - To display <mat-error> content for instance when you click on another control other than the submit,
      //                 simply mark the desired control(s) as touched in the handler of the button:
      this.paymentAutomaticForm.get('zipCodeFormControl').markAsTouched();
    } else {
        this.paymentAutomaticForm.get('nameFormControl').clearValidators();
        this.paymentAutomaticForm.get('addressLine1FormControl').clearValidators();
        this.paymentAutomaticForm.get('cityFormControl').clearValidators();
        this.paymentAutomaticForm.get('stateFormControl').clearValidators();
        this.paymentAutomaticForm.get('zipCodeFormControl').clearValidators();
        this.paymentAutomaticForm.get('countryFormControl').clearValidators();
        this.paymentAutomaticForm.get('bankRoutingNumberFormControl').clearValidators();
        this.paymentAutomaticForm.get('bankAccountNumberFormControl').clearValidators();
        this.paymentAutomaticForm.get('bankAccountNumberConfirmFormControl').clearValidators();
        this.paymentAutomaticForm.get('bankAccountNumberEqualsFormControl').clearValidators();
        this.paymentAutomaticForm.get('bankAccountTypeFormControl').clearValidators();
    }
}

/**
 * Routine to create the Braintree's credit card hosted fields
 */
private createBrainTreeHostedFields() {

  let lenderProgramCodes: string[] = [];
  lenderProgramCodes.push(this._autoPaymentSelected.selectedData.loanProgram);
  this._brainTreeLenderProgramCode = this._braintreeService.getBrainTreeLenderProgramCode(lenderProgramCodes);

  this._paymentService.getBrainTreeClientToken(this._brainTreeLenderProgramCode).subscribe(
    response => {
      this._clientToken = response.clientToken;

      if (this._logging.serviceDataTraceLog) {
        console.log("Braintree Client Token: " + this._clientToken);
      }
    },
    error => {
      this._handleError('createBrainTreeHostedFields :: getBrainTreeClientToken', error);
    },
    () => {

      // Received client token from Braintree so let's have braintree inject it's IFrames
      this.createBrainTreeUI();
    }
  );
}

 /**
   * Routine to create the Braintree's UI. Braintree will inject it's Iframes into the html
   */
private createBrainTreeUI() {
  this.showBraintreeSpinner = true;

  braintree.client.create({
    authorization: this._clientToken,
  }).then((clientInstance: any) => {

    // Device data
    braintree.dataCollector.create({
      client:clientInstance
    }).then((dataCollectorInstance) => {
      this._dataCollectorInstance = dataCollectorInstance;
    });

    // Hosted fields
    braintree.hostedFields.create({
      client: clientInstance,
      //preventAutofill: true,

      // Override styles for the hosted fields
      styles: {
        'input': {
          'font-size': '14px',
          'font-family': 'roboto, "Helvetica Neue", sans-serif',
          'color': 'rgb(102, 102, 102)',
        },
        '.expirationMonth': {
          'line-height': '3em',
          'height': '3em',
        },
        '.expirationYear': {
          'line-height': '3em',
          'height': '3em',
        }
      },

      // The hosted fields
      fields: {
        number: {
          container: '#credit-card-number',
        },
        expirationMonth: {
          container: '#expiration-month',
          select: true,
        },
        expirationYear: {
          container: '#expiration-year',
          select: true,
        },
         cvv: {
         container: '#cvv',
        },
      }

    }).then((hostedFieldsInstance) => {
      this._hostedFieldsInstance = hostedFieldsInstance;

      // Braintree provides months; however, numeric values not prefixed with zero
      (hostedFieldsInstance as any).setMonthOptions(this.creditCardExpMonthList);

      // TODO - Not being applied
      hostedFieldsInstance.addClass('expirationMonth', 'to-expiration-date', (addClassError) => {
        if (addClassError) {
          console.error(addClassError);
        }
      });

      hostedFieldsInstance.on('focus', (event) => {
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.createBrainTreeUI :: onFocus Emitted By = ' + event.emittedBy);
        }
        const field = event.fields[event.emittedBy];
        const emittedBy = event.emittedBy;
        this._brainTreeFieldFocus = event.emittedBy;

        this._braintreeService.onFocus(field, emittedBy);

      });

      hostedFieldsInstance.on('blur', (event) => {
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.createBrainTreeUI :: onBlur Emitted By = ' + event.emittedBy);
        }
        const field = event.fields[event.emittedBy];
        const emittedBy = event.emittedBy;

        this._braintreeService.onBlur(field, emittedBy);

        // Determine if we can enable 'Submit Payment' button; With the combination of Material and HTML objects,
        // we must programmatically set the disable property. Injected IFrames not part of the Reactive Forms so
        // the disabled property is not bound to the form control to be updated.
        this._brainTreeFieldsValid = this._braintreeService.isBraintreeFieldsValid(this._hostedFieldsInstance);
        this._braintreeService.enableDisablePaymentButton(this.SubmitButton.nativeElement,
                                                          this._brainTreeFieldsValid,
                                                          this.paymentAutomaticForm.valid);

                                                          this.cdr.detectChanges();

      });

      hostedFieldsInstance.on('empty', (event) => {
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.createBrainTreeUI :: onEmpty Emitted By = ' + event.emittedBy);
        }
        const field = event.fields[event.emittedBy];

        this._braintreeService.onEmpty(field);

      });

      hostedFieldsInstance.on('validityChange', (event) => {
        if (this._logging.serviceCallTraceLog) {
          console.log('PaymentAutomaticFormComponent.createBrainTreeUI :: onValidityChange Emitted By = ' + event.emittedBy);
        }
        const field = event.fields[event.emittedBy];
        const emittedBy = event.emittedBy;

        this._braintreeService.onValidityChange(field, emittedBy, this._brainTreeFieldFocus);

        // Determine if we can enable 'Submit Payment' button; With the combination of Material and HTML objects,
        // we must programmatically set the disable property. Injected IFrames not part of the Reactive Forms so
        // the disabled property is not bound to the form control to be updated.
        this._brainTreeFieldsValid = this._braintreeService.isBraintreeFieldsValid(this._hostedFieldsInstance);
        this._braintreeService.enableDisablePaymentButton(this.SubmitButton.nativeElement,
                                                          this._brainTreeFieldsValid,
                                                          this.paymentAutomaticForm.valid);

                                                          this.cdr.detectChanges();

      });

      this._brainTreeUIRenderComplete = true;
      this.showBraintreeSpinner = false;

      if (this._logging.serviceCallTraceLog) {
        console.log("Braintree UI Complete - Render Complete:" + this._brainTreeUIRenderComplete);
      }

    }).catch((error) => {
      this._handleError('createBrainTreeUI :: hostedFields.create', error);
    });

  });
}

// 03/18/2021 RA - Add method to reload the state field filtered by country
reset_state(newcountry: string) {
  this.paymentAutomaticForm.controls['stateFormControl'].setValue(this.getStateCountryList(newcountry));
  this.paymentAutomaticForm.controls['stateFormControl'].markAsDirty();
  this.paymentAutomaticForm.controls['stateFormControl'].markAsTouched();
  this.paymentAutomaticForm.controls['stateFormControl'].updateValueAndValidity();

  // 04/08/2021 RA
  this.paymentAutomaticForm.controls['zipCodeFormControl'].markAsDirty();
  this.paymentAutomaticForm.controls['zipCodeFormControl'].markAsTouched();
  this.paymentAutomaticForm.controls['zipCodeFormControl'].updateValueAndValidity();
}

// 03/12/2021 RA - setPaymentZipCodeValidation will set the validators for Payment zip code based on the provided country.
setPaymentZipCodeValidation(country: string = null): void {
   const validators: ValidatorFn[] = [
     Validators.minLength(this.getPaymentZipCodeMinLength(country)),
     Validators.maxLength(this.getPaymentZipCodeMaxLength(country))
   ];

   // Validators.required(),   -- 03/26/2021 RA Removed to do below
   // 03/29/2021 RA - Add new Zip code required validation only for USA and CAN.
   if (country === 'USA' || country === 'CAN') {
      validators.push(Validators.required);
    } else {
      this.paymentAutomaticForm.get('zipCodeFormControl').clearValidators();
    }

   // 03/12/2021 RA - A valid canadian postcode is 7 digits –
   //   in the format A1A 1A1, where A is a letter and 1 is a digit.
   //   a space separates the third and fourth characters.
   //   do not include the letters D, F, I, O, Q or U.
   //   the first position does not make use of the letters W or Z.
   // 03/29/2021 RA - Added Foreign Regex to be any Alphanumeric or Numeric or Blank
   //
   if (country !== null && country.trim().toUpperCase() === 'CAN') {
      const canadianZipCodeRegex = /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/;
      validators.push(Validators.pattern(canadianZipCodeRegex));
   }

   if (country !== null && country.trim().toUpperCase() === 'USA') {
      const usaZipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
      validators.push(Validators.pattern(usaZipCodeRegex));
   }

   if (country !== null && country.trim().toUpperCase() === 'FOR') {
    const forZipCodeRegex = /^([a-zA-Z0-9][\s\\/-]*){0,10}$/;
    validators.push(Validators.pattern(forZipCodeRegex));
   }
   this.paymentAutomaticForm.controls['zipCodeFormControl'].markAsDirty();
   this.paymentAutomaticForm.get('zipCodeFormControl').setValidators(validators);
   this.paymentAutomaticForm.get('zipCodeFormControl').updateValueAndValidity();
}

// 03/12/2021 RA - PaymentZipCodeOnlyAllowsNumbers checks to see if zip code entry allows numbers (used in the HTML file)
PaymentZipCodeOnlyAllowsNumbers(): boolean {
   if (this.paymentAutomaticForm.get('countryFormControl').value !== null
      && String(this.paymentAutomaticForm.get('countryFormControl').value).toUpperCase() !== 'USA') {
     return false;
   } else {
       return true;
     }
}

// 03/12/2021 RA - get ZipCodeMinLength returns the min zip code length based on country
// 03/25/2021 RA - Broke out Foreign into its own category
getPaymentZipCodeMinLength(country: string = null): number {
  if (country !== null) {
    if (country.trim().toUpperCase() === 'USA') {
        return this.fieldValidations.contact.zipCodeMinLength_USA;
    } else if (country.trim().toUpperCase() === 'CAN') {
        return this.fieldValidations.contact.zipCodeMinLength_CAN;
    } else if (country.trim().toUpperCase() === 'FOR') {
        return this.fieldValidations.contact.zipCodeMinLength_FOR;
    } else {
        return this.fieldValidations.contact.zipCodeMinLength;
    }
  } else {
      return this.fieldValidations.contact.zipCodeMinLength;
  }
}

// 03/12/2021 RA - getPaymentZipCodeMaxLength returns the max zip code length based on country
// 03/29/2021 RA - Broke out Foreign into its own category
getPaymentZipCodeMaxLength(country: string = null): number {
  if (country !== null) {
    if (country.trim().toUpperCase() === 'USA') {
      return this.fieldValidations.contact.zipCodeMaxLength_USA;
    } else if (country.trim().toUpperCase() === 'CAN') {
      return this.fieldValidations.contact.zipCodeMaxLength_CAN;
    } else if (country.trim().toUpperCase() === 'FOR') {
      return this.fieldValidations.contact.zipCodeMaxLength_FOR;
    } else {
      return this.fieldValidations.contact.zipCodeMaxLength;
    }
  } else {
    return this.fieldValidations.contact.zipCodeMaxLength;
  }
}

paymentTypeChanged(newPaymentType: PaymentType) {
  this.paymentAutomaticForm.get('bankRoutingNumberFormControl').clearValidators();
  this.paymentAutomaticForm.get('bankAccountNumberFormControl').clearValidators();
  this.paymentAutomaticForm.get('bankAccountNumberConfirmFormControl').clearValidators();
  this.paymentAutomaticForm.get('bankAccountNumberEqualsFormControl').clearValidators();
  this.paymentAutomaticForm.get('bankAccountTypeFormControl').clearValidators();

  if (newPaymentType === PaymentType.ElectronicCheck) {
    this.paymentAutomaticForm
        .get('bankRoutingNumberFormControl')
        .setValidators([
          Validators.required,
          Validators.minLength(this.fieldValidations.payment.bank.bankRoutingNumberMinLength),
          Validators.maxLength(this.fieldValidations.payment.bank.bankRoutingNumberMaxLength)
        ]);
    this.paymentAutomaticForm
        .get('bankAccountNumberFormControl')
        .setValidators([
          Validators.required,
          Validators.minLength(this.fieldValidations.payment.bank.bankAccountNumberMinLength),
          Validators.maxLength(this.fieldValidations.payment.bank.bankAccountNumberMaxLength)
        ]);
    this.paymentAutomaticForm
        .get('bankAccountNumberConfirmFormControl')
        .setValidators([
          Validators.required,
          Validators.minLength(this.fieldValidations.payment.bank.bankAccountNumberMinLength),
          Validators.maxLength(this.fieldValidations.payment.bank.bankAccountNumberMaxLength)
        ]);
    this.paymentAutomaticForm.get('bankAccountNumberEqualsFormControl').setValidators([Validators.requiredTrue]);
  } else {

      // Create Braintree's HostedFields. Needed to recreate when switching between CC and electronic payment
      // Routine is called when changing payment or selecting another note so lets insure we are on payment form switching types.
      if (this._brainTreeUIRenderComplete && this._brainTreeLastPaymentType === PaymentType.ElectronicCheck) {
        this._brainTreeUIRenderComplete = false;
        this._braintreeService.tearDownBraintree(this._hostedFieldsInstance);
        this.createBrainTreeHostedFields();
      }
    }

    this.paymentAutomaticForm.get('bankRoutingNumberFormControl').updateValueAndValidity();
    this.paymentAutomaticForm.get('bankAccountNumberFormControl').updateValueAndValidity();
    this.paymentAutomaticForm.get('bankAccountNumberConfirmFormControl').updateValueAndValidity();
    this.paymentAutomaticForm.get('bankAccountNumberEqualsFormControl').updateValueAndValidity();
    this.paymentAutomaticForm.get('bankAccountTypeFormControl').updateValueAndValidity();
  }

  bankAccountNumberConfirmChanged(newBankAccountNumberConfirm: string) {
    this.paymentAutomaticForm.controls['bankAccountNumberEqualsFormControl'].setValue(this.areBankAccountNumbersEqual());
    this.paymentAutomaticForm.controls['bankAccountNumberEqualsFormControl'].markAsDirty();
    this.paymentAutomaticForm.controls['bankAccountNumberEqualsFormControl'].markAsTouched();
    this.paymentAutomaticForm.get('bankAccountNumberEqualsFormControl').updateValueAndValidity();
  }

  setup(): void {
    this._getAutoPaymentDetail(true, false)
  }

  cancel(): void {
    this._getAutoPaymentDetail(false, true)
  }

  isPaymentTypeCreditCard(): boolean {
    return !(this.paymentAutomaticForm.controls['paymentTypeFormControl'].value === PaymentType.ElectronicCheck);
  }

  areBankAccountNumbersEqual(): boolean {
    return (
      this.paymentAutomaticForm.controls['bankAccountNumberFormControl'].value ===
      this.paymentAutomaticForm.controls['bankAccountNumberConfirmFormControl'].value
    );
  }

  // 10/11/2021 - Get Company Code
  CompanyCode_Check(): boolean {
    for (const companyLookup of environment.companyCode.legaltext ) {
      if (companyLookup.companyCode === this.companyCode ) {
        return true;
      }
    }
    return false;
  }

  private _getAutoPaymentDetail(isAutoPaymentSetup: boolean, isAutoPaymentCancel: boolean) {

    // With the mixture of Reactive form controls & Non-Reactive form controls (Hosted Fields), rapid clicks was not disabling button fast enough
    if (isAutoPaymentSetup) {
      this.SubmitButton.nativeElement.disabled = true;
      if (this._isSubmitted) {
        return;
      }
      this._isSubmitted = true;
    }

    const autoPaymentDetail = this._paymentService.getNewAutoPaymentDetail();
    autoPaymentDetail.participantId = this._participantId;
    autoPaymentDetail.noteId = this.noteId;
    autoPaymentDetail.name = this.paymentAutomaticForm.controls['nameFormControl'].value;
    autoPaymentDetail.addressLine1 = this.paymentAutomaticForm.controls['addressLine1FormControl'].value;
    autoPaymentDetail.addressLine2 = this.paymentAutomaticForm.controls['addressLine2FormControl'].value;
    autoPaymentDetail.city = this.paymentAutomaticForm.controls['cityFormControl'].value;
    autoPaymentDetail.state = this.paymentAutomaticForm.controls['stateFormControl'].value;
    autoPaymentDetail.zipCode = this.paymentAutomaticForm.controls['zipCodeFormControl'].value;
    autoPaymentDetail.paymentAmount = null;
    autoPaymentDetail.paymentDueDay = null;

    // Single loan not group from store proc str_sel_set_autopay_info
    autoPaymentDetail.loanProgram = this._autoPaymentSelected.selectedData.loanProgram;

    // Required to control application flow (i.e. electronic payment)
    autoPaymentDetail.paymentType = this.paymentAutomaticForm.controls['paymentTypeFormControl'].value;

    autoPaymentDetail.bankRoutingNumber = this.paymentAutomaticForm.controls['bankRoutingNumberFormControl'].value;
    autoPaymentDetail.bankAccountNumber = this.paymentAutomaticForm.controls['bankAccountNumberFormControl'].value;
    autoPaymentDetail.bankAccountConfirmNumber = this.paymentAutomaticForm.controls['bankAccountNumberConfirmFormControl'].value;
    autoPaymentDetail.bankAccountType = this.paymentAutomaticForm.controls['bankAccountTypeFormControl'].value;
    autoPaymentDetail.userName = this._userService.getUserName();
    autoPaymentDetail.paymentRef = null;
    autoPaymentDetail.confirmationCode = null;
    autoPaymentDetail.responseCode = null;
    autoPaymentDetail.responseMessage = null;
    autoPaymentDetail.confirmationNumbers = null;
    autoPaymentDetail.firstPaymentAmount = this.noteAutoPayInformation.scheduleOnePaymentAmount;
    autoPaymentDetail.firstPaymentDate = this.noteAutoPayInformation.scheduleOneStartDate;
    autoPaymentDetail.isAutoPaymentSetup = isAutoPaymentSetup;
    autoPaymentDetail.isAutoPaymentCancel = isAutoPaymentCancel;

    // Credit Card information needs to be tokenized via Braintree API
    if (this.isPaymentTypeCreditCard() && isAutoPaymentSetup) {

      if (this._hostedFieldsInstance == null) {
        var error = {errDesc: "HostedFieldsInstance is null. Can't create Braintree CC properties"};
        this._handleError('submitPayment', error);
      }
      this._hostedFieldsInstance.tokenize((tokenizeErr, payload) => {
        if (tokenizeErr) {
            this._handleError('submitPayment', tokenizeErr, payload);
        } else {

            if (this._logging.serviceDataTraceLog) {
              console.log("Braintree tokenized payload: " + JSON.stringify(payload));
            }
            autoPaymentDetail.nonce = payload.nonce;
            autoPaymentDetail.partiFirstName = this._userService.getFirstName();
            autoPaymentDetail.partiLastName = this._userService.getLastName();
            autoPaymentDetail.partiEmail = this._userService.getEmail();
            autoPaymentDetail.deviceData = this._dataCollectorInstance.deviceData;
            autoPaymentDetail.creditCardType = payload.details.cardType;
            autoPaymentDetail.lastFourCreditCard = payload.details.lastFour;

            // Not consistent with providing leading zero for single digits
            autoPaymentDetail.creditCardExpMonth = ('0' + payload.details.expirationMonth).slice(-2);
            autoPaymentDetail.creditCardExpYear = payload.details.expirationYear;
            autoPaymentDetail.creditCardBin = payload.details.bin;
            autoPaymentDetail.brainTreePaymentType = payload.type;
            autoPaymentDetail.brainTreeLenderProgramCode = this._brainTreeLenderProgramCode;

            this.setupAutoPayment.emit(autoPaymentDetail);
        }
      });
    } else {

      // Required to emit data within method to accomodate braintree promise (tokenization)
      if (isAutoPaymentSetup) {
        this.setupAutoPayment.emit(autoPaymentDetail);
      } else {
        // Hardcoded to Visa as it was prior to Braintree implementation
        autoPaymentDetail.creditCardType = BraintreeCreditCardType.Visa;

        // Obtain the Braintree credit card type
        let lenderProgramCodes: string[] = [];
        lenderProgramCodes.push(this._autoPaymentSelected.selectedData.loanProgram);
        let brainTreeLenderProgramCode = this._braintreeService.getBrainTreeLenderProgramCode(lenderProgramCodes);

        this._paymentService.GetBraintreeDefaultCreditCardInfo(brainTreeLenderProgramCode, this._participantId, this.noteId.toString(), true).subscribe(
          response => {

            // Obtain the default credit card description; Default to Visa which was always hardcoded before
            if (response != null) {
              autoPaymentDetail.creditCardType = response.creditCardTypeDesc;
            }

            if (this._logging.serviceDataTraceLog) {
              console.log('_getAutoPaymentDetail :: GetBraintreeDefaultCreditCardInfo :: result = ' + JSON.stringify(response));
            }
          },
          error => {
            this._handleError('_getAutoPaymentDetail :: GetBraintreeDefaultCreditCardInfo', error);

            // No reason to show customer any issues
            this.cancelAutoPayment.emit(autoPaymentDetail);
          },
          () => {
            this.cancelAutoPayment.emit(autoPaymentDetail);
          }
        );
      }
    }
  }

  // Validate Angular form controls and Braintree fields
  isFormValid() {

    if (this.isPaymentTypeCreditCard()) {
    return !(this._brainTreeFieldsValid && this.paymentAutomaticForm.valid);
    } else {
        if (this.isPaymentTypeCreditCard()) {
          return !(this._brainTreeFieldsValid && this.paymentAutomaticForm.valid);
        } else {
          return !(this.paymentAutomaticForm.valid);
        }
    }
  }
}
